import { Injectable, signal } from '@angular/core';

export interface EncryptionPromptState {
  reason: string;
  errorMessage: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SessionVaultService {
  private readonly hasStoredSnapshotState = signal(false);
  private readonly encryptionPasswordState = signal<string | null>(null);
  private readonly apiKeyState = signal<string | null>(null);
  private readonly encryptionPromptStateInternal = signal<EncryptionPromptState | null>(null);

  private pendingEncryptionRequest: Promise<boolean> | null = null;
  private resolvePendingEncryptionRequest: ((value: boolean) => void) | null = null;

  readonly hasStoredSnapshot = this.hasStoredSnapshotState.asReadonly();
  readonly encryptionPromptState = this.encryptionPromptStateInternal.asReadonly();

  getEncryptionPassword(): string | null {
    return this.encryptionPasswordState();
  }

  getApiKey(): string | null {
    return this.apiKeyState();
  }

  hasEncryptionPassword(): boolean {
    return this.encryptionPasswordState() !== null;
  }

  hasApiKey(): boolean {
    return this.apiKeyState() !== null;
  }

  rememberEncryptionPassword(password: string): void {
    this.encryptionPasswordState.set(password);
  }

  rememberApiKey(apiKey: string): void {
    const normalizedValue = apiKey.trim();
    this.apiKeyState.set(normalizedValue.length > 0 ? normalizedValue : null);
  }

  clearSessionSecrets(): void {
    this.encryptionPasswordState.set(null);
    this.apiKeyState.set(null);
  }

  setHasStoredSnapshot(hasStoredSnapshot: boolean): void {
    this.hasStoredSnapshotState.set(hasStoredSnapshot);
  }

  async requestEncryptionPassword(reason: string): Promise<boolean> {
    if (this.hasEncryptionPassword()) {
      return true;
    }

    if (this.pendingEncryptionRequest) {
      return this.pendingEncryptionRequest;
    }

    this.encryptionPromptStateInternal.set({
      reason,
      errorMessage: null,
    });

    this.pendingEncryptionRequest = new Promise<boolean>((resolve) => {
      this.resolvePendingEncryptionRequest = resolve;
    });

    return this.pendingEncryptionRequest;
  }

  submitEncryptionPassword(password: string): void {
    const normalizedPassword = password.trim();

    if (normalizedPassword.length === 0) {
      this.reportEncryptionPromptError('Encryption password is required.');
      return;
    }

    this.encryptionPasswordState.set(normalizedPassword);
    this.finishPendingEncryptionRequest(true);
  }

  cancelEncryptionPasswordRequest(): void {
    this.finishPendingEncryptionRequest(false);
  }

  reportEncryptionPromptError(message: string): void {
    const currentPrompt = this.encryptionPromptStateInternal();

    if (!currentPrompt) {
      return;
    }

    this.encryptionPromptStateInternal.set({
      ...currentPrompt,
      errorMessage: message,
    });
  }

  private finishPendingEncryptionRequest(result: boolean): void {
    const resolver = this.resolvePendingEncryptionRequest;

    this.pendingEncryptionRequest = null;
    this.resolvePendingEncryptionRequest = null;
    this.encryptionPromptStateInternal.set(null);
    resolver?.(result);
  }
}